import { supabase, applyOrgFilter } from '../supabaseClient'
import { normalizeSportValue } from '../../constants/sports'
import { isVisiblePlayer } from '../playerVisibility'
import { getJugadoresInscritos } from './temporadaInscripciones'

// Campos minimos para decidir visibilidad con isVisiblePlayer.
const SELECT_VISIBILIDAD = 'id, activo, deleted, estatus'

// Mismo criterio de "jugador vigente" que usa la pantalla (isVisiblePlayer):
// activo, no borrado y sin estatus de baja. Vive aqui y no solo en la vista
// porque PlayerFormModal deja poner estatus='Baja' sin tocar `activo`: ese
// jugador desaparece de las listas pero seguia entrando en la resolucion y
// recibiendo cargos (la fila decia "2 jugadores" y el servicio le cobraba a 3).
// Se filtra con el helper de JS en vez de repetir el criterio en SQL para que no
// existan dos definiciones que se puedan separar.
const soloVisibles = async (jugadorIds) => {
  if (!jugadorIds.length) return []
  let q = supabase
    .from('jugadores')
    .select(SELECT_VISIBILIDAD)
    .in('id', jugadorIds)
  q = applyOrgFilter(q)
  const { data, error } = await q
  if (error) throw error
  const vigentes = new Set((data || []).filter(isVisiblePlayer).map((j) => j.id))
  return jugadorIds.filter((id) => vigentes.has(id))
}

// Traduce "lo que el admin marco" (jugadores, familias o grupos) a la lista de
// jugadores a los que se les generara el cargo. Siempre pasa por el padron de la
// temporada Y por el criterio de visibilidad: nadie fuera de el recibe cargos.
export const resolverJugadores = async ({ unidad, ids = [], temporadaId }) => {
  const marcados = [...new Set(ids)]
  if (!marcados.length || !temporadaId) return []

  if (unidad === 'grupo') {
    // Por grupo el padron ya resuelve la membresia VIVA (ver 20260818).
    const porGrupo = await Promise.all(
      marcados.map((grupoId) => getJugadoresInscritos(temporadaId, { grupoId })),
    )
    return soloVisibles([...new Set(porGrupo.flat())])
  }

  const inscritos = new Set(await getJugadoresInscritos(temporadaId))

  if (unidad === 'jugador') return soloVisibles(marcados.filter((id) => inscritos.has(id)))

  if (unidad === 'familia') {
    let q = supabase
      .from('jugadores')
      .select(SELECT_VISIBILIDAD)
      .in('familia_id', marcados)
    q = applyOrgFilter(q)
    const { data, error } = await q
    if (error) throw error
    return (data || [])
      .filter(isVisiblePlayer)
      .map((j) => j.id)
      .filter((id) => inscritos.has(id))
  }

  return []
}

// Que planes tiene ya cada jugador en la temporada. Alimenta la cobertura y el
// filtro "sin plan / con este plan / con otro plan".
export const getPlanesDeJugadores = async (temporadaId) => {
  let q = supabase
    .from('pagos_jugador_plan')
    .select('jugador_id, plan_id')
    .eq('activo', true)
    .eq('temporada_id', temporadaId)
  q = applyOrgFilter(q)
  const { data, error } = await q
  if (error) throw error
  const mapa = {}
  ;(data || []).forEach((r) => {
    if (!mapa[r.jugador_id]) mapa[r.jugador_id] = []
    mapa[r.jugador_id].push(r.plan_id)
  })
  return mapa
}

// Los inscritos de cada grupo, para pintar la cobertura ("18 inscritos, 15 con
// plan") sin una consulta por grupo. Solo grupos del deporte de la temporada:
// un jugador puede estar en un grupo de tackle y a la vez inscrito en la
// temporada de flag, y el cruce con el padron no lo distingue (si esta
// inscrito, solo que en otro deporte). Sin este filtro, elegir el grupo de
// tackle le cobraria el plan de flag. Antes esta validacion vivia en el
// servicio de cobro y se retiro; ahora se resuelve aqui, impidiendo la
// eleccion equivocada en vez de reportarla despues.
export const getInscritosPorGrupo = async (temporadaId) => {
  if (!temporadaId) return {}

  const { data: temporada, error: tErr } = await supabase
    .from('temporadas')
    .select('deporte_id')
    .eq('id', temporadaId)
    .single()
  if (tErr) throw tErr
  if (!temporada) return {}

  // El deporte se compara NORMALIZADO (mismo criterio que inscripcionRows y
  // gruposVivosDeInscripcion): el alias legado 'equipado' vale 'tackle'. Con la
  // comparacion cruda, un grupo con el valor viejo se caia del allowlist y con
  // el de la vista Grupos Y del dropdown "Grupo" de la vista Jugadores, sin
  // avisar. Por eso el filtro es en JS y no un .eq() en SQL: Postgres no conoce
  // los alias.
  let gQ = supabase
    .from('grupos')
    .select('id, deporte')
  gQ = applyOrgFilter(gQ)
  const { data: grupos, error: gErr } = await gQ
  if (gErr) throw gErr
  const deporte = normalizeSportValue(temporada.deporte_id)
  const grupoIds = (grupos || [])
    .filter((g) => normalizeSportValue(g.deporte) === deporte)
    .map((g) => g.id)
  if (!grupoIds.length) return {}

  // jugadores_grupos no tiene org_id: no lleva applyOrgFilter, queda acotada
  // por el in(grupoIds) de arriba, que si viene filtrado por org.
  const [{ data: membresias, error: mErr }, inscritos] = await Promise.all([
    supabase
      .from('jugadores_grupos')
      .select('jugador_id, grupo_id')
      .in('grupo_id', grupoIds)
      .eq('activo', true)
      .or('deleted.is.null,deleted.eq.false'),
    getJugadoresInscritos(temporadaId),
  ])
  if (mErr) throw mErr

  const inscritosSet = new Set(inscritos)
  // Mismo criterio de visibilidad que resolverJugadores: sin esto la cobertura
  // ("18 inscritos · 15 con plan") contaba a los dados de baja y el boton
  // "N sin plan" los mandaba a cobro.
  const vigentes = new Set(await soloVisibles(
    [...new Set((membresias || []).map((m) => m.jugador_id))],
  ))
  const porGrupo = {}
  ;(membresias || []).forEach((m) => {
    if (!inscritosSet.has(m.jugador_id)) return
    if (!vigentes.has(m.jugador_id)) return
    if (!porGrupo[m.grupo_id]) porGrupo[m.grupo_id] = []
    porGrupo[m.grupo_id].push(m.jugador_id)
  })
  return porGrupo
}

// Jugadores con al menos un cargo VENCIDO en la temporada. Alimenta el filtro
// "con adeudo". Un cargo 'pendiente' aun no vence, asi que no cuenta como
// adeudo (mismo criterio que getEstadoJugadoresGrupo, en
// temporadaInscripciones.js).
export const getJugadoresConAdeudo = async (temporadaId) => {
  if (!temporadaId) return new Set()
  let q = supabase
    .from('pagos_cargos')
    .select('jugador_id')
    .eq('temporada_id', temporadaId)
    .eq('estatus', 'vencido')
  q = applyOrgFilter(q)
  const { data, error } = await q
  if (error) throw error
  return new Set((data || []).map((c) => c.jugador_id))
}
